import { DelegateMapEntry } from 'containers/Base/ActionDelegate';
import { Product } from 'data/terra/Product';
import { Action } from 'redux';
import { ThunkAction } from 'redux-thunk';
import Terra from 'services/apis/terra/Terra';
import { RootState } from 'store/reducer';
import { ProductSelectorState } from '../reducer';
import { PAGE_SIZE } from './FetchProducts';
import { uniqBy } from 'lodash';

enum ActionTypes {
  loadMore = 'products/loadMore',
  loadMoreSuccess = 'products/loadMoreSuccess',
  loadMoreFailure = 'products/loadMoreFailure',
}

export interface FetchMoreProductsAction {
  type: ActionTypes;
  nextProducts?: Product[];
  currentPage?: number;
  error?: string;
}

export const fetchMoreProducts = (): ThunkAction<
  void,
  RootState,
  unknown,
  Action<string>
> => async (dispatch, getState) => {
  const {
    hasInitialized,
    hasMore,
    currentPage,
    isLoadingMore,
  } = getState().productSelector;

  if (!hasInitialized || !hasMore || isLoadingMore) return;

  dispatch({ type: ActionTypes.loadMore });
  const result = await Terra.product.fetchProducts({
    page: currentPage + 1,
    pageSize: PAGE_SIZE,
  });
  if (result.isSuccess()) {
    dispatch({
      type: ActionTypes.loadMoreSuccess,
      nextProducts: result.get()?.data,
      currentPage: currentPage + 1,
    });
  } else {
    dispatch({
      type: ActionTypes.loadMoreFailure,
      error: result.exception().message,
    });
  }
};

export const fetchMoreProductsDelegate: DelegateMapEntry<
  FetchMoreProductsAction,
  ProductSelectorState
> = {
  [ActionTypes.loadMore]: (
    state: ProductSelectorState,
    action: FetchMoreProductsAction
  ): ProductSelectorState => {
    return {
      ...state,
      isLoadingMore: true,
      loadMoreError: undefined,
    };
  },
  [ActionTypes.loadMoreSuccess]: (
    state: ProductSelectorState,
    action: FetchMoreProductsAction
  ): ProductSelectorState => {
    const nextProducts = action.nextProducts ?? [];
    return {
      ...state,
      products: uniqBy([...state.products, ...nextProducts], item => item.id),
      currentPage: action.currentPage ?? 0,
      isLoadingMore: false,
      hasMore: nextProducts.length === PAGE_SIZE,
    };
  },
  [ActionTypes.loadMoreFailure]: (
    state: ProductSelectorState,
    action: FetchMoreProductsAction
  ): ProductSelectorState => {
    return {
      ...state,
      isLoadingMore: false,
      loadMoreError: action.error,
    };
  },
};
